import React, { FC, useEffect, useMemo } from 'react';
import { Grid } from 'react-styled-flexboxgrid';
import { useSelector } from 'react-redux';
import { useCustomRouter } from '@hooks/useCustomRouter';
import { useTranslations } from '@hooks/useTranslations';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { useGTM } from '@hooks/useGTM';
import { CarJourneyType } from 'src/services';
import Main from '@components/Layout/Main';
import TopNavbar from '@components/TopNavbar';
import TrimCarousel from '@components/TrimCarousel';
import FilterBox from '../../FilterBox/Configurable';
import FAQs from '@components/FAQs';
import CustomError from '@components/CustomError';
import { BRAND, BRAND_TYPE, IS_B2B, isBrandAC, isBrandOV } from 'src/constants/main';
import routes from 'src/constants/routes';
import TrimSelectorDuck from 'src/redux/trimSelector/trimSelector.duck';
import FilterDuck from 'src/redux/filters/filter.duck';
import GlobalDuck from 'src/redux/global/global.duck';
import { Redux } from 'src/redux';
import UIDuck from 'src/redux/commonDucks/ui.duck';
import { buildParameterizedPathname } from '@utils/url.utils';
import { setPageTitleDescriptionProperties } from '@utils/appConfig.utils';
import styled, { breakpoints } from 'src/styles/theme';
import { rem } from 'polished';
import { BrandTypes } from 'src/types/brands';
import { ERROR_CODE } from 'src/pages/_error';
import { FEATURES_LIST, IFeatureSwitchContext } from 'src/context/featureSwitch';
import PriceLegalText from '@components/PriceLegalText';
import { useGetNotEscapedTranslationMarkup } from '@hooks/useGetNotEscapedTranslationMarkup';
import { GtmData } from 'src/types/gtm';
import { getOfferCarTitle } from '@components/OfferListItem/utils/getOfferCartitle';
import { NeedHelpSection } from '@components/NeedHelpSection';
import { ISelectorPageProps } from 'src/pages/trim/configurable/[nameplateBodyStyleSlug]';

const StyledGrid = styled(Grid)<{ brand: BRAND_TYPE }>`
    padding: 0 10px;
    box-sizing: border-box;
    max-width: 100%;

    @media (max-width: calc(1200px - 0.1px)) {
        width: 100% !important;
    }

    @media (min-width: 1200px) {
        width: 76rem;
    }

    @media (min-width: 1300px) {
        width: 76rem;
    }

    @media (max-width: ${breakpoints.xs}px) {
        margin-top: 0;
    }
`;

const ConfigurableTrimPage: FC<ISelectorPageProps> = ({ nameplateBodyStyleSlug }) => {
    const { t } = useTranslations();
    const router = useCustomRouter();
    const { pushProductImpressions, pushTrimPageLoad, pushToDataLayer } = useGTM();
    const carJourney = useSelector<Redux, CarJourneyType>((state) => GlobalDuck.getCarJourney(state));

    const allTrims = useSelector((state: Redux) => TrimSelectorDuck.getOwnState(state).configurable.trims);
    const filteredTrims = useSelector((state: Redux) => TrimSelectorDuck.getFilteredTrims(state));
    const filteredOutTrims = useSelector((state: Redux) => TrimSelectorDuck.getFilteredOutTrims(state));
    const trimConfigurations = useSelector((state: Redux) => TrimSelectorDuck.getTrimConfigurations(state));
    const filters = useSelector((state) => FilterDuck.getFilters(state));
    const isLoading = useSelector((state) => UIDuck.isLoading(state));
    const { getNotEscapedTranslationMarkup } = useGetNotEscapedTranslationMarkup();

    const sampleFilteredTrim = filteredTrims[0];
    const fullBrand = t('title');

    const usingFilters = filters.filter((filter) => filter.value).length > 0;

    const pageTitleDescriptionProperties = useMemo(
        () => setPageTitleDescriptionProperties(fullBrand, sampleFilteredTrim),
        [fullBrand, sampleFilteredTrim]
    );

    const handleBackButtonClick = () => {
        const queryString = router.asPath.includes('?')
            ? router.asPath.substring(router.asPath.indexOf('?'), router.asPath.length)
            : '';
        const returnPath = buildParameterizedPathname(`${routes.ROOT}${queryString}`, carJourney);

        router.push(returnPath);
    };

    useEffect(() => {
        pushTrimPageLoad(allTrims, false);
        pushToDataLayer({
            eventCategory: 'SolRetail::VehicleFinition',
            eventAction: 'Select::VehicleModel::NonInteractive',
            eventLabel: 'Vehicle model selected',
        } as GtmData);
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        pushProductImpressions('trim', filteredTrims);
    }, [filteredTrims]);

    if (!allTrims.length) {
        return (
            <Main title={String(ERROR_CODE.NOT_FOUND)}>
                <CustomError
                    errorType={String(ERROR_CODE.NOT_FOUND)}
                    titleDescription={t('car.fetch.error')}
                    returnLink={buildParameterizedPathname(routes.ROOT, carJourney)}
                    returnLinkLabel={t('errorPage.returnLink.label')}
                />
                <NeedHelpSection />
                <FAQs />
            </Main>
        );
    }

    const pageCanonicalLink = buildParameterizedPathname(routes.SELECTOR, carJourney, {
        nameplateBodyStyleSlug,
    });

    const shouldIncludeBodyStyleDescription = useFeatureSwitchEnabled(
        FEATURES_LIST.FEATURE_SWITCH_SHOW_GR_BODY_STYLE_IN_CAR_TITLE
    );

    return (
        <Main
            title={
                IS_B2B
                    ? t('seo.trimPage.b2b.title', pageTitleDescriptionProperties)
                    : t('seo.trimPage.title', pageTitleDescriptionProperties)
            }
            description={
                IS_B2B
                    ? t('seo.trimPage.b2b.description', pageTitleDescriptionProperties)
                    : t('seo.trimPage.description', pageTitleDescriptionProperties)
            }
            canonicalLink={pageCanonicalLink}
        >
            <TopNavbar
                title={t('topNavbar.trimTitle')}
                step={t('topNavbar.trimStep')}
                onGoBack={handleBackButtonClick}
            />
            <StyledGrid brand={BRAND}>
                <FilterBox filters={filters} />

                <StyledDivider brand={BRAND} count={filteredTrims.length}>
                    <h3>{getOfferCarTitle(sampleFilteredTrim, shouldIncludeBodyStyleDescription)}</h3>
                </StyledDivider>

                <StyledSubtitle brand={BRAND}>
                    {t('trims.counterWithoutCurrent', { total: filteredTrims.length })}
                </StyledSubtitle>

                <div id="skip-to-content" style={{ marginBottom: '30px' }}>
                    <TrimCarousel
                        filteredTrims={filteredTrims}
                        filteredOutTrims={filteredOutTrims}
                        disabled={isLoading}
                        trimConfigurations={trimConfigurations}
                        usingFilters={usingFilters}
                    />
                </div>
                <PriceLegalText className="frontpage-legaltext-over">
                    <p className="frontpage-legaltext">
                        <span dangerouslySetInnerHTML={getNotEscapedTranslationMarkup('filters.legal.text')} />
                    </p>
                </PriceLegalText>
            </StyledGrid>
            <FAQs />
        </Main>
    );
};

export default ConfigurableTrimPage;
const StyledSubtitle = styled.h5<{ brand: BRAND_TYPE }>`
    font-weight: normal;
    text-transform: uppercase;
    width: 100%;
    text-align: center;
    margin-bottom: ${rem(10)};
    font-size: 80%;

    @media (max-width: ${breakpoints.sm}px) {
        display: none;
    }
`;

const StyledDivider = styled.div<{ brand: BRAND_TYPE; count: number }>`
    display: flex;
    overflow: hidden;
    margin-bottom: ${({ count }) => (count ? rem(10) : rem(25))};

    @media (max-width: ${breakpoints.sm}px) {
        margin-top: ${rem(-15)};
        margin-bottom: ${rem(15)};
    }

    h3 {
        font-family: ${({ theme, brand }) => {
            if (BrandTypes.OV) return theme.fonts.fontBase;
            if (BrandTypes.DS) return theme.fonts.fontBase;
            if (BrandTypes.AP) return theme.fonts.fontBase;
            if (BrandTypes.AC) return theme.fonts.fontBase;
        }};

        font-size: ${({ brand }) => (isBrandOV ? rem(24) : rem(20))};
        line-height: ${rem(28)};
        text-transform: uppercase;
        margin: 0 auto;
        position: relative;
        padding: 0 ${rem(50)};

        @media (max-width: ${breakpoints.sm}px) {
            padding: 0 ${rem(20)};
        }

        ${({ brand }) =>
            isBrandOV &&
            `
            font-weight: 500
        `};

        ${({ brand }) =>
            !isBrandOV &&
            `
            &::before,
            &::after {
                content: '';
                position: absolute;
                width: 800%;
                height: 1px;
                background: #b4b4b4;
                top: 50%;
                transform: translateY(-50%);
            }
            &::after {
                right: -800%;
            }
            &::before {
                left: -800%;
            }
        `};

        ${({ brand, theme }) =>
            isBrandAC &&
            `
            color: ${theme.colors.grey5};
            font-size: ${rem(22)};
            &::before,
            &::after {
                content: '';
                position: absolute;
                width: 800%;
                height: 1px;
                background: #796f6e;
                top: 50%;
                transform: translateY(-50%);
            }
            &::after {
                right: -800%;
            }
            &::before {
                left: -800%;
            }
        `};

        @media (min-width: ${breakpoints.sm}px) {
            ${({ brand }) =>
                isBrandOV &&
                `
                    &::before,
                    &::after {
                        content: '';
                        position: absolute;
                        width: 800%;
                        height: 1px;
                        background: #b4b4b4;
                        top: 50%;
                        transform: translateY(-50%);
                    }
                    &::after {
                        right: -800%;
                    }
                    &::before {
                        left: -800%;
                    }
                `}
        }
    }
`;
